/* cf-preview-scroll.js
 * Click Fencing - a long fence must SCROLL, not shrink.
 * Pete 2026-08-28: "can you scroll the graphics if they go longer than the screen allows?"
 *
 * Measured on live 2026-08-28, blade at 30,000mm on a 1585px desktop:
 *   .live-preview-wrapper .visual-preview#section-preview svg { width: 100%; max-height: 130px }
 * forces the drawing to the container width, so the whole thing is scaled DOWN as the fence
 * gets longer - viewBox 2808 wide rendered at 1277px, a scale of 0.455, and the 13-unit
 * dimension labels landed at 5.9 CSS px. Unreadable. cf-mobile already overrides this on a
 * phone (that is why mobile scrolled and desktop did not), so the bug was desktop-only and
 * invisible on the short runs everyone tests with.
 *
 * The fix is deliberately SURGICAL: it only intervenes once fitting the fence to the panel
 * would push the drawing below a readable scale. A run that still fits at or above that
 * scale is left exactly as it was, so nothing changes for the short and medium runs that
 * already looked right. A longer run is pinned to the floor scale (a 150px band on desktop,
 * 128px on mobile), its width follows the fence, and .panels-container - already
 * overflow-x: auto - scrolls. Each dimension label is sized in user units so it lands at
 * ~11.5 CSS px at that scale, whatever the calculator's viewBox height is.
 * Verified: 4m unchanged and centred; 30m svg 2045px in a 1277px box, labels 11.5px,
 * scrollbar present.
 *
 * A scale drawing must never be squashed to fit - see snippets/cf-mobile.liquid, which
 * cost a full round trip learning exactly that.
 *
 * 2026-08-29 - GLASS. Pete: "the frameless glass - when you have a section that is large, the
 * graphics change to tiny. the graphics should stay large like the aluminium fences." Glass
 * has no preview <svg> at all: it draws the fence as plain divs inside .panels-container, each
 * with an inline px width the calculator scales down to fit. Measured on an emulated iPhone 14
 * Pro at 20,000mm, every 1800mm panel rendered 21.6px wide and the whole 20m fence sat in
 * 400px - 0.012 px/mm, against 0.072 px/mm for blade at the same length. So the second half of
 * this file gives glass the same deal: pin to the floor scale, let #section-preview scroll.
 *
 * Loads AFTER cf-preview-legible and deliberately overrides its width:100% rule.
 * Display only - touches no price, product, quantity or cart line.
 */
(function () {
  if (window.__cfPreviewScrollPatched) return;
  window.__cfPreviewScrollPatched = true;

  var SCOPE = '#section-preview svg, .visual-preview svg, .section-preview-wrapper svg, .panels-container svg';
  var TARGET_LABEL_PX = 11.5;

  function bandHeight() { return window.innerWidth <= 749 ? 128 : 150; }

  function restore(svg) {
    svg.style.removeProperty('height');
    svg.style.removeProperty('width');
    svg.style.removeProperty('max-width');
    svg.style.removeProperty('max-height');
    svg.style.removeProperty('flex');
    svg.style.removeProperty('margin');
    var ts = svg.querySelectorAll('text[data-cf-fs0]');
    for (var i = 0; i < ts.length; i++) {
      ts[i].setAttribute('font-size', ts[i].getAttribute('data-cf-fs0'));
      ts[i].removeAttribute('data-cf-fs0');
    }
  }

  // How much room the drawing actually has. NOT svg.parentElement.clientWidth: .panels-container
  // is sized to its content, so the moment we pin the svg wide the parent reports that same
  // width back, the "it fits" test flips to true, we restore to width:100%, the parent snaps
  // back to the viewport, we pin again - the drawing flickers big/small forever, roughly once
  // a second on the safety-net interval. Pete 2026-08-29: "the graphics keep flickering from
  // big to small". The stable number is the narrowest box in the chain, i.e. the scrollport.
  function availWidth(svg) {
    var w = 0, e = svg.parentElement, hops = 0;
    while (e && e !== document.body && hops < 10) {
      var cw = e.clientWidth;
      if (cw > 0) w = (w === 0) ? cw : Math.min(w, cw);
      if (e.classList && e.classList.contains('live-preview-wrapper')) break;
      e = e.parentElement; hops++;
    }
    return w;
  }

  function doSvg(svg, H) {
    /* NOT THE GLASS RAKE PANEL. Added 2026-09-02.

       SCOPE includes ".panels-container svg", which on the frameless glass calculator matches
       the little per-panel artwork drawn for a RAKED panel - not the fence-wide drawing this
       file was written for. Pinning that to the 150px band (128 on a phone) makes the rake
       panel a fixed height regardless of the plain <div> panels standing beside it, so it
       never lined up; and because cf-glass-preview-fixes.js pushed the intrinsic size back on
       its own schedule while this ran on a permanent 1.2s interval, the two alternated and
       the panel flickered. Pete, 2026-09-02: "the rake panels still look awful ... also it
       cant flicker like it does. fix this for good."

       A rake panel is sized from its neighbours, which is cf-rake-align.js's job. The
       fence-wide drawing this file does own never sits inside a .panel-preview. */
    if (svg.closest && svg.closest('.panel-preview')) return;

    var vb = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number);
    if (vb.length !== 4 || !vb[2] || !vb[3]) return;
    var scale = H / vb[3];                       // the floor: below this, labels stop reading
    var avail = availWidth(svg);
    // Hysteresis. Pinned width is round(vb[2] * scale), so "does it fit" sits exactly on the
    // knife edge of >= and a single rounded pixel decides it. Once pinned, demand 2% of clear
    // room before handing the drawing back to fit-to-width. Big is the state to stay in - a
    // shrunk fence is unreadable, which is the whole reason this patch exists.
    var pinned = !!(svg.style.width && svg.style.width !== 'auto');
    var need = pinned ? scale * 1.02 : scale;
    // a run that still fits at or above the floor scale is none of our business
    if (avail && avail / vb[2] >= need) { restore(svg); return; }
    var W = Math.round(vb[2] * scale);

    svg.style.setProperty('height', H + 'px', 'important');
    svg.style.setProperty('width', W + 'px', 'important');
    svg.style.setProperty('max-width', 'none', 'important');
    svg.style.setProperty('max-height', 'none', 'important');
    svg.style.setProperty('flex', 'none', 'important');

    // we only get here when the run overflows, so start it at the left: the customer sees
    // the beginning of their fence and scrolls to the rest
    svg.style.setProperty('margin', '0', 'important');

    var want = Math.round((TARGET_LABEL_PX / scale) * 10) / 10;
    var texts = svg.querySelectorAll('text');
    for (var i = 0; i < texts.length; i++) {
      var t = texts[i];
      if (t.getAttribute('data-cf-gate')) continue;   // annotations size themselves
      var cur = parseFloat(t.getAttribute('font-size') || 0);
      if (!cur || cur > 60) continue;
      if (!t.getAttribute('data-cf-fs0')) t.setAttribute('data-cf-fs0', cur);
      if (Math.abs(cur - want) > 0.2) t.setAttribute('font-size', want);
    }

    var box = svg.closest('#section-preview, .visual-preview, .panels-container, .section-preview-wrapper');
    while (box) {
      box.style.setProperty('overflow-x', 'auto', 'important');
      box.style.setProperty('overscroll-behavior-x', 'contain');
      box = box.parentElement && box.parentElement.closest('#section-preview, .visual-preview, .panels-container, .section-preview-wrapper');
    }
  }

  // ── Glass: same floor, no svg ────────────────────────────────────────────────────────────
  // .panels-container is a flex row of .gap-preview boxes and unclassed panel wrappers.
  // Gaps are drawn at a fixed 13px minimum, not to scale, so they are left alone; stretching
  // them would only push the panels apart. Every panel's ORIGINAL width is stamped in
  // data-cf-w0 and the scale is always recomputed from that, never from the width we last
  // wrote, or each pass would compound and the fence would grow without limit.
  var GLASS_FLOOR = 0.072;    // px per mm - what blade/flat top/mesh get at 20m on a phone
  var W0 = 'data-cf-w0';

  function panelMm(el) {
    var lbl = el.querySelector('.panel-width');
    if (!lbl) return 0;
    var m = /(\d[\d.]*)/.exec(lbl.textContent || '');
    return m ? parseFloat(m[1]) : 0;
  }

  function doGlass(pc) {
    var kids = pc.children, panels = [], scale = 0, i, k, w0, mm;
    for (i = 0; i < kids.length; i++) {
      k = kids[i];
      if (k.classList && k.classList.contains('gap-preview')) continue;
      w0 = k.getAttribute(W0);
      if (w0 === null) {
        w0 = parseFloat(k.style.width);
        if (!w0) continue;                     // not one of the calculator's sized boxes
        k.setAttribute(W0, w0);
      } else {
        w0 = parseFloat(w0);
      }
      mm = panelMm(k);
      panels.push({ el: k, w0: w0 });
      if (mm > 0 && w0 / mm > scale) scale = w0 / mm;
    }
    if (!panels.length || !scale) return;

    var mult = scale >= GLASS_FLOOR ? 1 : GLASS_FLOOR / scale;
    for (i = 0; i < panels.length; i++) {
      var p = panels[i];
      if (mult === 1) {
        if (p.el.style.getPropertyPriority('width') === 'important') {
          p.el.style.setProperty('width', p.w0 + 'px');
          p.el.style.removeProperty('flex');
        }
        continue;
      }
      var want = Math.round(p.w0 * mult * 10) / 10;
      if (Math.abs(parseFloat(p.el.style.width) - want) > 0.05) {
        p.el.style.setProperty('width', want + 'px', 'important');
        p.el.style.setProperty('flex', '0 0 auto', 'important');
      }
    }
  }

  function apply() {
    var H = bandHeight();
    var svgs = document.querySelectorAll(SCOPE);
    for (var i = 0; i < svgs.length; i++) { try { doSvg(svgs[i], H); } catch (e) {} }
    var pcs = document.querySelectorAll('.panels-container');
    for (var j = 0; j < pcs.length; j++) { try { doGlass(pcs[j]); } catch (e) {} }
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    (window.requestAnimationFrame || setTimeout)(function () { queued = false; apply(); }, 0);
  }

  try {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true, subtree: true, characterData: true
    });
  } catch (e) {}
  window.addEventListener('resize', schedule);

  // Permanent safety net - the observer alone missed a redraw on 2026-08-28 (see
  // cf-gate-hinge-latch.js for the evidence), and a customer sits on this page for minutes.
  setInterval(apply, 1200);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
  apply();
  console.log('[cf-preview-scroll] installed');
})();
